import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Bearer token fallback (also helpful if cookies don't persist across ingress)
const TOKEN_KEY = "dnf_access_token";
export function setAccessToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY);
}

api.interceptors.request.use((cfg) => {
  const t = getAccessToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// On 401 redirect to login (except for /auth endpoints).
// IMPORTANTE: algunos endpoints integran servicios externos (WhatsApp/Meta, FacturaDirecta,
// Wialon, Cloudinary...) que pueden devolver 401 cuando es Meta/FD quien rechaza, no nuestra
// sesión. Whitelist de paths donde un 401 NO significa "sesión expirada".
const EXTERNAL_SERVICE_PATHS = [
  "/whatsapp/",
  "/fd/",
  "/facturadirecta",
  "/wialon",
  "/cloudinary",
  "/places/",
  "/ors/",
  "/maps/",
];

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    const status = err?.response?.status;
    const isAuthPath = url.includes("/auth/");
    const isExternalService = EXTERNAL_SERVICE_PATHS.some((p) => url.includes(p));
    if (status === 401 && !isAuthPath && !isExternalService) {
      setAccessToken(null);
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo ha fallado. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const ROLE_LABELS = {
  admin: "Admin",
  comercial: "Comercial",
  "diseño": "Diseño",
  diseno: "Diseño",
  produccion: "Producción",
  administracion: "Administración",
  montador: "Montador",
  logistica: "Logística",
  gestoria: "Gestoría",
};

export const ESTADO_PETICION = {
  solicitado_cliente: { label: "Solicitado por cliente", color: "bg-violet-600/25 text-violet-200 border border-violet-500/50" },
  borrador: { label: "Borrador", color: "bg-zinc-700 text-zinc-200" },
  pendiente_validacion: { label: "Pend. validación taller", color: "bg-amber-600/20 text-amber-200 border border-amber-500/40" },
  presupuesto_aprobado: { label: "Pres. aprobado", color: "bg-purple-600/20 text-purple-300 border border-purple-500/40" },
  aprobada: { label: "Aprobada", color: "bg-blue-600/20 text-blue-300 border border-blue-500/40" },
  en_produccion: { label: "En producción", color: "bg-orange-600/20 text-orange-300 border border-orange-500/40" },
  en_ruta: { label: "En ruta", color: "bg-cyan-600/20 text-cyan-300 border border-cyan-500/40" },
  montado_parcial: { label: "Montaje parcial", color: "bg-yellow-600/20 text-yellow-200 border border-yellow-500/40" },
  entregada: { label: "Montado", color: "bg-emerald-600/20 text-emerald-300 border border-emerald-500/40" },  // Flujo v2 — Fernando 31/05/26
  facturada: { label: "Facturada", color: "bg-teal-600/20 text-teal-300 border border-teal-500/40" },
  cancelada: { label: "Cancelada", color: "bg-rose-600/20 text-rose-300 border border-rose-500/40" },
  // Legacy aliases (datos antiguos — se siguen mostrando con su color, pero se filtran como activos)
  presupuesto: { label: "Presupuesto", color: "bg-amber-600/20 text-amber-300 border border-amber-500/40" },
  presupuesto_enviado: { label: "Presupuesto enviado", color: "bg-amber-600/20 text-amber-300 border border-amber-500/40" },
  pendiente: { label: "Pendiente (legacy)", color: "bg-zinc-700 text-zinc-200" },
  planificada: { label: "Planificada (legacy)", color: "bg-blue-600/20 text-blue-300 border border-blue-500/40" },
  finalizada: { label: "Finalizada (legacy)", color: "bg-emerald-600/20 text-emerald-300 border border-emerald-500/40" },
  cerrada: { label: "Cerrada", color: "bg-zinc-600/30 text-zinc-300 border border-zinc-500/40" },
};

// Estados que el USUARIO puede SELECCIONAR manualmente (Flujo v2 — Fernando 31/05/26)
// El resto los gestiona el sistema automáticamente.
export const ESTADOS_PETICION_SELECCIONABLES = ["borrador", "aprobada", "cancelada"];
export const ESTADOS_PETICION_AUTO = [
  "presupuesto_aprobado", "en_produccion", "en_ruta", "montado_parcial", "entregada", "facturada",
];

// Estados que se consideran "activos" (visibles en el listado por defecto)
export const ESTADOS_PETICION_ACTIVOS = new Set([
  "solicitado_cliente",
  "borrador", "presupuesto_aprobado", "aprobada", "en_produccion",
  "en_ruta", "montado_parcial", "entregada",
  // Legacy considerados activos para no perder visibilidad de datos antiguos
  "presupuesto", "presupuesto_enviado", "pendiente", "planificada",
]);

export const TIPO_LINEA = {
  diseno: { label: "Diseño", color: "bg-violet-600/20 text-violet-300" },
  diseno_produccion: { label: "Diseño + Producción", color: "bg-indigo-600/20 text-indigo-300" },
  diseno_produccion_montaje: { label: "Diseño + Prod. + Montaje", color: "bg-blue-600/20 text-blue-300" },
  produccion_montaje: { label: "Producción + Montaje", color: "bg-teal-600/20 text-teal-300" },
  solo_montaje: { label: "Solo Montaje", color: "bg-emerald-600/20 text-emerald-300" },
  otros: { label: "Otros", color: "bg-zinc-600/30 text-zinc-300" },
  retirada: { label: "Retirada", color: "bg-orange-600/20 text-orange-300" },
  // Fase 1 flujo DNF v2: envío sin montaje
  produccion_envio: { label: "Producción + Envío", color: "bg-fuchsia-600/20 text-fuchsia-300" },
  diseno_produccion_envio: { label: "Diseño + Prod. + Envío", color: "bg-pink-600/20 text-pink-300" },
  // legacy aliases para compatibilidad
  montaje: { label: "Solo Montaje", color: "bg-emerald-600/20 text-emerald-300" },
  desmontaje: { label: "Desmontaje (legacy)", color: "bg-zinc-700/60 text-zinc-400" },  // Fernando 31/05/26: migrar a retirada
  produccion: { label: "Producción (legacy)", color: "bg-zinc-700/60 text-zinc-400" },
  envio: { label: "Envío (legacy)", color: "bg-zinc-700/60 text-zinc-400" },
  incidencia: { label: "Incidencia", color: "bg-red-600/25 text-red-300" },
  "2a_visita": { label: "2ª visita", color: "bg-indigo-600/20 text-indigo-300" },
};

// Tipos ACTIVOS ordenados según preferencia Fernando 31/05/26 (1=más usado)
export const TIPOS_LINEA_ORDEN = [
  "solo_montaje",               // 1
  "produccion_montaje",          // 2
  "diseno_produccion_montaje",   // 3
  "retirada",                    // 4
  "produccion_envio",            // 5
  "diseno_produccion_envio",
  "diseno",
  "diseno_produccion",
  "otros",
];
// Tipos DEPRECADOS — no ofrecer al crear nuevas líneas
export const TIPOS_LINEA_DEPRECADOS = ["desmontaje", "produccion", "envio", "montaje", "incidencia", "2a_visita"];

export const ESTADO_LINEA = {
  pendiente: "bg-zinc-700/60 text-zinc-300",
  programada: "bg-blue-700/30 text-blue-300",
  agendada: "bg-indigo-700/30 text-indigo-300",
  en_curso: "bg-amber-600/30 text-amber-300",
  completada: "bg-emerald-700/30 text-emerald-300",
  enviado: "bg-fuchsia-700/30 text-fuchsia-300",
  cerrada_sin_montaje: "bg-amber-700/30 text-amber-300",
  cancelada: "bg-red-700/30 text-red-300",
};

export const ESTADO_COMPONENTE = {
  pendiente: { label: "Pendiente", color: "bg-zinc-700/60 text-zinc-300", icon: "•" },
  pendiente_diseno: { label: "Pend. Diseño", color: "bg-violet-600/20 text-violet-300", icon: "✏️" },
  en_diseno: { label: "En diseño", color: "bg-violet-600/40 text-violet-100", icon: "🎨" },
  aaff_pendiente_cliente: { label: "AAFF pend. cliente", color: "bg-amber-600/20 text-amber-300", icon: "⏳" },
  aaff_recibido: { label: "AAFF recibido", color: "bg-amber-600/40 text-amber-100", icon: "✅" },
  en_produccion: { label: "En producción", color: "bg-orange-600/30 text-orange-200", icon: "🏭" },
  producido: { label: "Producido", color: "bg-emerald-600/30 text-emerald-200", icon: "📦" },
  pendiente_proveedor: { label: "Pend. proveedor", color: "bg-blue-600/20 text-blue-300", icon: "🚚" },
  recibido_almacen: { label: "En almacén", color: "bg-blue-600/40 text-blue-100", icon: "📥" },
  recibido_pdv: { label: "Recibido en PDV", color: "bg-cyan-600/30 text-cyan-200", icon: "📍" },
  cliente_aporta: { label: "Aporta cliente", color: "bg-fuchsia-600/30 text-fuchsia-200", icon: "🎁" },
  en_logistica: { label: "Logística", color: "bg-teal-600/30 text-teal-200", icon: "🚛" },
  entregado: { label: "Entregado", color: "bg-emerald-700/50 text-emerald-100", icon: "✔" },
  cancelado: { label: "Cancelado", color: "bg-red-600/30 text-red-200", icon: "✕" },
  // legacy
  listo: { label: "Listo", color: "bg-blue-700/30 text-blue-300", icon: "•" },
  entregado_legacy: { label: "Entregado", color: "bg-emerald-700/30 text-emerald-300", icon: "•" },
};

export const ORIGEN_LABELS = {
  produccion_propia: { label: "Producción propia", color: "bg-emerald-600/20 text-emerald-300 border-emerald-500/40", icon: "🏭" },
  proveedor_a_nave: { label: "Proveedor → Nave", color: "bg-blue-600/20 text-blue-300 border-blue-500/40", icon: "🚚" },
  cliente_a_nave: { label: "Cliente → Nave", color: "bg-sky-600/20 text-sky-300 border-sky-500/40", icon: "📦" },           // Flujo v2 — Fernando 31/05/26
  cliente_a_pdv: { label: "Cliente → PDV", color: "bg-indigo-600/20 text-indigo-300 border-indigo-500/40", icon: "🏬" },    // Flujo v2 — Fernando 31/05/26
  ya_en_pdv: { label: "Ya en PDV", color: "bg-zinc-600/30 text-zinc-300 border-zinc-500/40", icon: "📍" },
  cliente_aporta: { label: "Aporta cliente", color: "bg-fuchsia-600/20 text-fuchsia-300 border-fuchsia-500/40", icon: "🎁" },
  // DEPRECADOS — siguen en BD pero no se ofrecen en formularios nuevos
  proveedor_a_cliente: { label: "Proveedor → Cliente (legacy)", color: "bg-zinc-700/60 text-zinc-400 border-zinc-600/40", icon: "📦" },
  externo: { label: "Externo (legacy)", color: "bg-zinc-700/60 text-zinc-400 border-zinc-600/40", icon: "📦" },
};

// Orígenes "activos" (ofrecidos al crear/editar) — sin legacy
export const ORIGENES_ACTIVOS = [
  "produccion_propia", "proveedor_a_nave",
  "cliente_a_nave", "cliente_a_pdv",
  "ya_en_pdv", "cliente_aporta",
];

export const PLANTILLA_LABELS = {
  libre: "Libre",
  marco_vinilo: "Marco de vinilo (4 piezas)",
  podium_1x1: "Podium 1×1 (3 piezas)",
  pancarta: "Pancarta",
};
